import useDocumentHead from '../hooks/useDocumentHead';
import { seoFor, PUBLIC_PAGES } from '../content/pageSeo';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download } from '../components/ui/icons';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Button from '../components/ui/Button';
import LivingFrame from '../components/home/LivingFrame';
import PricingSection from '../components/pricing/PricingSection';
import { buildProductSchema } from '../content/structuredData';
import HOME from '../content/homeContent.json';
import ARTICLE_ROUTES from '../content/articleRoutes.generated.json';
import api from '../utils/api';

// One shared page container so every section reads on the same measure/gutters.
const SHELL = 'mx-auto w-full max-w-[1240px] px-5 sm:px-8';
// Consistent vertical rhythm between major sections.
const SECTION_Y = 'py-16 sm:py-20 lg:py-28';
// Section heading scale — Archivo Black is a heavy face, so it runs smaller than
// the old light serif did at the same visual weight.
const H2 = { fontSize: 'clamp(1.75rem, 2.8vw, 2.5rem)', lineHeight: 1.1 };

// Hero heading, hero standfirst, and the intro line above the pricing read from the
// same manifest scripts/prerender.mjs reads, so the prerendered homepage and
// the live one can never say something different. Mirrors how ArticlesIndexPage
// reads its heading/standfirst from PUBLIC_PAGES.articles.
// The h1 is the plain description people search for ("Funeral & memorial video maker"),
// shown as a small label; the brand line (TAGLINE) stays the big words above the
// buttons. Search engines weigh the h1 most, and the brand line says nothing about
// what the site does (founder decision, 2026-10-05).
const { heading: HEADING, tagline: TAGLINE, standfirst: STANDFIRST, introHeading: INTRO_HEADING, introBody: INTRO_BODY } =
  PUBLIC_PAGES.home;
const IMG = (name) => `${process.env.PUBLIC_URL}/preview/${name}`;

const LandingPage = () => {
  // The three packages as Product + Offers, from the same tiers.json the cards render.
  useDocumentHead({ ...seoFor('home'), jsonLd: [buildProductSchema()] });

  useEffect(() => { api.get('/health').catch(() => {}); }, []);

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Navbar />

      {/* The page's main landmark (between the navbar and the footer), for screen readers. */}
      <main>
      {/* ── HERO — cream ─────────────────────────────────────────────────── */}
      <section className="relative bg-brand-cream pt-28 pb-16 sm:pt-32 sm:pb-20 lg:pb-24">
        <div className={SHELL}>
          <div className="grid lg:grid-cols-[minmax(0,52fr)_minmax(0,48fr)] gap-10 lg:gap-16 items-center">

            {/* Copy — left-aligned (incl. mobile, per brief) */}
            <div className="text-left">
              <h1 className="font-sans text-label font-bold uppercase tracking-widest text-secondary mb-3">
                {HEADING}
              </h1>
              <p
                className="font-display text-brand-ink [text-wrap:balance]"
                style={{ fontSize: 'clamp(2rem, 3.6vw, 3.1rem)', lineHeight: 1.08 }}
              >
                {TAGLINE}
              </p>

              <p className="mt-6 mx-auto lg:mx-0 max-w-[54ch] text-brand-ink text-body-lg leading-relaxed [text-wrap:pretty]">
                {STANDFIRST}
              </p>

              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <Button as={Link} to="/register" size="lg" className="whitespace-nowrap">
                  Start your mixtape
                </Button>
                <Button as={Link} to="/guide" variant="secondary" size="lg" className="whitespace-nowrap">
                  Read the guide
                </Button>
              </div>

              {/* Proof line — Figma hero. "Free to start" leads: naming the price
                  is what makes the sign-up click feel safe. */}
              <p className="mt-6 text-secondary text-label [text-wrap:balance]">
                {HOME.proofLine.map((item, i) => (
                  <React.Fragment key={item}>
                    {i > 0 && <>{' '}<span aria-hidden="true">·</span>{' '}</>}
                    {item}
                  </React.Fragment>
                ))}
              </p>
            </div>

            {/* Living Frame — the polaroid collage (capped so the hero fits the first screen) */}
            <div className="w-full max-w-[560px] mx-auto lg:mx-0 lg:justify-self-end">
              <LivingFrame />
            </div>
          </div>
        </div>
      </section>

      {/* ── 1. THE INTRO LINE + PRICING — paper ─────────────────────────── */}
      {/* The three options are the centrepiece (27 Sep 2026 pricing brief). Each card
          carries its own "How it works", so there is no separate how-it-works or
          "Simple to make" section any more. scroll-mt clears the fixed navbar for
          links to /#pricing. */}
      <section aria-labelledby="pricing-heading" className={`bg-brand-paper scroll-mt-20 ${SECTION_Y}`}>
        <div className={SHELL}>
          <div className="text-center mb-12">
            <h2 id="pricing-heading" className="text-brand-ink [text-wrap:balance] mx-auto max-w-[24ch]" style={H2}>
              {INTRO_HEADING}
            </h2>
            <p className="mt-5 mx-auto max-w-[58ch] text-secondary text-body-lg leading-relaxed [text-wrap:pretty]">
              {INTRO_BODY}
            </p>
          </div>
          <PricingSection id="pricing" className="scroll-mt-24" />
        </div>
      </section>

      {/* ── 2. MADE FOR THE OCCASION — cream ─────────────────────────────── */}
      <section className={`bg-brand-cream ${SECTION_Y}`}>
        <div className={SHELL}>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <img
                src={IMG('memorial-landscape-06.webp')}
                // 640px copy from scripts/optimise-images.js; never shown wider than ~600px.
                srcSet={`${IMG('memorial-landscape-06-640.webp')} 640w, ${IMG('memorial-landscape-06.webp')} 1280w`}
                sizes="(min-width: 1024px) 600px, 100vw"
                width={1280}
                height={721}
                alt={HOME.occasion.imageAlt}
                loading="lazy"
                decoding="async"
                className="w-full aspect-video object-cover rounded-[6px] border-[6px] border-brand-cream bg-brand-rose shadow-photo rotate-[2deg]"
              />
            </div>
            <div>
              <h2 className="text-brand-ink [text-wrap:balance]" style={H2}>
                {HOME.occasion.heading}
              </h2>
              <p className="mt-4 max-w-[52ch] text-secondary text-body-lg leading-relaxed [text-wrap:pretty]">
                {HOME.occasion.body}
              </p>
              <div className="mt-6 inline-flex items-start gap-3 text-secondary text-sm max-w-[46ch]">
                <Download className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>{HOME.occasion.note}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. THE GUIDE / BEFORE YOU BEGIN — paper ──────────────────────── */}
      <section className={`bg-brand-paper ${SECTION_Y}`}>
        <div className={SHELL}>
          <div className="text-center mb-10">
            <h2 className="text-brand-ink [text-wrap:balance]" style={H2}>
              {HOME.beforeYouBegin.heading}
            </h2>
            <p className="mt-3 mx-auto max-w-[58ch] text-secondary text-base">
              {HOME.beforeYouBegin.intro}
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 max-w-[1080px] mx-auto mb-10">
            {HOME.beforeYouBegin.cards.map(card => (
              <div key={card.label} className="card">
                <p className="text-label font-bold uppercase tracking-widest text-secondary mb-2">{card.label}</p>
                <h3 className="text-xl text-brand-ink mb-2 leading-snug">{card.title}</h3>
                <p className="text-secondary text-sm leading-relaxed">{card.body}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button as={Link} to="/guide" size="lg" variant="secondary">
              {HOME.beforeYouBegin.cta}
            </Button>
          </div>
        </div>
      </section>

      {/* ── 4. HELPFUL ARTICLES — cream ──────────────────────────────────── */}
      {/* Links straight to a few articles, so search engines see them linked from the
          homepage (Rank Math audit, 5 Oct 2026). Titles come from the articles
          themselves via articleRoutes.generated.json; the choice of articles is in
          homeContent.json. */}
      <section aria-labelledby="helpful-articles-heading" className={`bg-brand-cream ${SECTION_Y}`}>
        <div className={SHELL}>
          <div className="text-center mb-10">
            <h2 id="helpful-articles-heading" className="text-brand-ink [text-wrap:balance]" style={H2}>
              {HOME.helpfulArticles.heading}
            </h2>
            <p className="mt-3 mx-auto max-w-[58ch] text-secondary text-base">
              {HOME.helpfulArticles.intro}
            </p>
          </div>

          <ul className="grid gap-5 sm:grid-cols-2 max-w-[880px] mx-auto mb-10">
            {HOME.helpfulArticles.slugs.map(slug => (
              <li key={slug}>
                <Link
                  to={`/${slug}`}
                  className="card block h-full text-lg text-brand-ink leading-snug hover:underline underline-offset-4 decoration-2"
                >
                  {ARTICLE_ROUTES.titles[slug]}
                </Link>
              </li>
            ))}
          </ul>

          <div className="text-center">
            <Button as={Link} to="/articles" size="lg" variant="secondary">
              {HOME.helpfulArticles.allLabel}
            </Button>
          </div>
        </div>
      </section>

      {/* ── CTA — dark ink ───────────────────────────────────────────────── */}
      <section className="bg-brand-ink py-16 sm:py-20">
        <div className={`${SHELL} text-center`}>
          <h2 className="text-brand-cream [text-wrap:balance] mx-auto max-w-[20ch]" style={H2}>
            {HOME.closing.heading}
          </h2>
          <p className="mt-4 mx-auto max-w-[48ch] text-brand-cream/80 text-body-lg leading-relaxed">
            {HOME.closing.body}
          </p>
          <div className="mt-8">
            <Button as={Link} to="/register" size="lg">
              {HOME.closing.cta}
            </Button>
          </div>
        </div>
      </section>

      </main>
      {/* ── FOOTER — paper ───────────────────────────────────────────────── */}
      <Footer />
    </div>
  );
};

export default LandingPage;
